import { z } from "zod/v4";
import { apiFetch, ApiError } from "@/lib/apiClient";

export const queueNames = ["document-import", "drive", "push-notification-receipts"] as const;
export const jobStates = ["waiting", "active", "delayed", "failed", "completed"] as const;
export const queueFilters = ["all", ...queueNames] as const;
export const stateFilters = ["all", ...jobStates] as const;
export const stateLabels = { all: "All job states", waiting: "Waiting", active: "Processing", delayed: "Delayed", failed: "Failed (retained)", completed: "Completed (retained)" };
export const PAGE_SIZE = 25;
export const MAX_PAGE = 401; // Last permitted offset: 10,000.
export const queueSearchSchema = z.object({
  queue: z.enum(queueFilters).optional().catch(undefined),
  state: z.enum(stateFilters).catch("all"),
  page: z.coerce.number().int().min(1).max(MAX_PAGE).catch(1),
});
export type QueueSearch = z.infer<typeof queueSearchSchema>;
export function selectJobs(search: QueueSearch, selection: Partial<Pick<QueueSearch, "queue" | "state">>): QueueSearch {
  return { ...search, ...selection, page: 1 };
}
const count = z.number().int().nonnegative();
const date = z.iso.datetime();
export const summariesSchema = z.object({ fetchedAt: date, queues: z.array(z.object({
  name: z.enum(queueNames), waiting: count, active: count, delayed: count,
  failed: count, completed: count, pending: count, paused: z.boolean(),
})).refine(queues => queues.length === queueNames.length && new Set(queues.map(queue => queue.name)).size === queueNames.length, "Expected all configured queues") });
export const jobsSchema = z.object({ fetchedAt: date, queue: z.enum(queueFilters), state: z.enum(stateFilters), offset: count, limit: count, total: count,
  jobs: z.array(z.object({ queue: z.enum(queueNames), state: z.enum(jobStates), id: z.string(), name: z.string(), createdAt: date, processedAt: date.nullable(), finishedAt: date.nullable(), attemptsMade: count })),
});
export function jobsPath(search: QueueSearch) {
  const valid = queueSearchSchema.parse(search);
  if (!valid.queue) throw new Error("Choose a queue to view jobs.");
  const params = new URLSearchParams({ queue: valid.queue, state: valid.state, offset: String((valid.page - 1) * PAGE_SIZE), limit: String(PAGE_SIZE) });
  return `/admin/queues/jobs?${params}`;
}
export async function getQueueSummaries(signal?: AbortSignal) {
  return summariesSchema.parse(await apiFetch("/admin/queues", { signal }));
}
export async function getQueueJobs(search: QueueSearch, signal?: AbortSignal) {
  const result = jobsSchema.parse(await apiFetch(jobsPath(search), { signal }));
  if (result.queue !== search.queue || result.state !== search.state || result.offset !== (search.page - 1) * PAGE_SIZE || result.limit !== PAGE_SIZE || result.jobs.length > PAGE_SIZE || result.jobs.some(job => (search.queue !== "all" && job.queue !== search.queue) || (search.state !== "all" && job.state !== search.state))) throw new Error("The queue job response could not be verified.");
  return result;
}
export function queueErrorMessage(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 503 && error.code === "QUEUES_UNAVAILABLE") return "Queue monitoring is unavailable. The queue service could not be read. Try again later.";
    if ([401, 403].includes(error.status)) return "Your administrator access could not be confirmed. Recheck access to continue.";
    if (error.status === 429) return "Too many requests. Automatic refresh is paused. Wait before refreshing manually.";
  }
  return "Queue monitoring could not be refreshed. Check your connection and try again.";
}
